// The settings screens (spec/13 §13, spec/14 §14; #454, #455; regrouped by job 2026-10-07): one per scope, generated
// from the registry. The screen is one request — `GET …/venues/{v}/settings`, `GET …/organizations/{o}/settings`, or
// HQ's `GET /v1/hq/settings` and `GET /v1/hq/organizations/{o}/settings` — and is laid out by the job a café owner is
// doing (`layout.ts`): Venue, Time & pricing, Sessions & PCs, Money, Cash & shifts, Food & orders, Players & sign-in,
// Staff & limits, Hardware, Security, Notifications, Privacy & records. The same groups on every screen, with the rows
// that screen shows.
//
//   /settings          the landing: on a venue's screen "Get started", then the search and every group
//   /settings/$group   one group: its sections, panels and rows; a search at the top finds any setting on the screen
//
// An old link that names a registry group (`…/settings/payments#wallet.minTopupAmount`) opens the job group the key
// sits in now.

import type { SettingsView } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState, type ComponentType, type ReactNode, type RefObject } from 'react';
import { PageHeader } from '@clavian/ui-web';

import { useApi } from '../api/client';
import { useMyVenues } from '../api/queries';
import type { Versioned } from '../api/versioned';
import { Loading } from '../shell/layouts';
import { LoadFailure } from '../shell/LoadFailure';
import { useConsoleScope, useCurrentVenue } from '../shell/scope';
import { useStationClasses } from '../stations/queries';
import { FIELDS } from '../venue-profile/fields';
import { VenueProfileGroup } from '../venue-profile/VenueProfileGroup';
import { BusinessDetailsGroup } from './BusinessDetailsGroup';
import { CountersGroup } from './CountersGroup';
import { SetupSummary } from '../setup/SetupSummary';
import { isJobGroup, JOB_GROUP_IDS, jobGroupOf, layoutGroup, placementOf, type JobGroupId } from './layout';
import { optionWords, useRegistryLabels, useSettingHelp } from './labels';
import { settingMoney } from './money';
import { AgeRulesNote, CleanupOffNote, DaysNote, LimitsNote, OfflinePlayNote, OnlinePaymentsStatusLine, PaygSummary, type ScreenLinks } from './panels';
import { PrintersGroup } from './PrintersGroup';
import { readSettings, registryPart, settingsKey, type RegistryWords, type SettingsScope } from './registry-source';
import { REVEAL_SETTING_EVENT, requestRevealSetting, revealWhenDrawn, settingKeyFromHash } from './reveal';
import { buildSettingsIndex, type IndexWords, type SettingDoc, type SettingsTarget } from './search';
import { SettingsForm, type OrganizationLink } from './SettingsForm';
import { SettingsSearchBox } from './SettingsSearchBox';
import { TaxCategoriesGroup } from './TaxCategoriesGroup';
import { taxCategoryControlFor } from './TaxCategoryControl';
import { TaxRegistrationGroup } from './TaxRegistrationGroup';
import { TOPUP_CONTROLS, TopUpPreview } from './TopUpSettings';

import './settings.css';
import type { ControlProps, SourceWords } from './types';

export { settingsKey } from './registry-source';

/** A settings screen's data, for any scope. */
export function useSettingsView(scope: SettingsScope, enabled = true) {
  const api = useApi();
  const key = settingsKey(scope);
  const query = useQuery({
    queryKey: key,
    queryFn: () => readSettings(api, scope),
    enabled,
    // A settings screen is edited, not watched: a refetch while typing only to find the editor's
    // base moved is what the ETag exists to catch at save time instead.
    refetchOnWindowFocus: false,
  });
  return { query, key };
}

export function VenueSettingsPage(): ReactNode {
  const venue = useCurrentVenue();
  const consoleScope = useConsoleScope();
  const { group } = useParams({ strict: false }) as { group?: string };
  const scope = useMemo<SettingsScope>(
    () => ({ kind: 'venue', organizationId: venue.organizationId, venueId: venue.id }),
    [venue.organizationId, venue.id],
  );
  const organization = consoleScope.organization;
  const links = useMemo<ScreenLinks>(
    () => ({
      pricing: { to: '/v/$venueSlug/pricing/$tab', params: { venueSlug: venue.slug, tab: 'payg' } },
      roles: { to: '/v/$venueSlug/staff', params: { venueSlug: venue.slug } },
      classes: { to: '/v/$venueSlug/stations', params: { venueSlug: venue.slug } },
      venueProfile: { to: '/v/$venueSlug/settings/$group', params: { venueSlug: venue.slug, group: 'venue' } },
    }),
    [venue.slug],
  );
  const organizationLink = useMemo<OrganizationLink | undefined>(
    () =>
      organization === undefined || organization.allVenues === false
        ? undefined
        : { to: '/o/$orgSlug/settings/$group', params: { orgSlug: organization.slug }, groupOf: (key: string) => placementOf(key).group },
    [organization],
  );
  return (
    <SettingsScreen
      scope={scope}
      group={group}
      groupLink={(id) => ({ to: '/v/$venueSlug/settings/$group', params: { venueSlug: venue.slug, group: id } })}
      landingLink={{ to: '/v/$venueSlug/settings', params: { venueSlug: venue.slug } }}
      links={links}
      {...(organizationLink === undefined ? {} : { organizationLink })}
    />
  );
}

export function OrganizationSettingsPage(): ReactNode {
  const consoleScope = useConsoleScope();
  const params = useParams({ strict: false }) as { group?: string };
  const organization = consoleScope.organization;
  const organizationId = organization?.id ?? '';
  const scope = useMemo<SettingsScope>(() => ({ kind: 'organization', organizationId }), [organizationId]);
  const slug = organization?.slug ?? '';
  const links = useMemo<ScreenLinks>(
    () => ({
      pricing: { to: '/o/$orgSlug/pricing/$tab', params: { orgSlug: slug, tab: 'payg' } },
      roles: { to: '/o/$orgSlug/staff', params: { orgSlug: slug } },
    }),
    [slug],
  );
  if (organization === undefined) return null;
  return (
    <SettingsScreen
      scope={scope}
      group={params.group}
      groupLink={(id) => ({ to: '/o/$orgSlug/settings/$group', params: { orgSlug: organization.slug, group: id } })}
      landingLink={{ to: '/o/$orgSlug/settings', params: { orgSlug: organization.slug } }}
      links={links}
    />
  );
}

/** Where a group of this screen lives: a route and its parameters. */
export interface GroupLink {
  readonly to: string;
  readonly params: Readonly<Record<string, string>>;
}

export interface SettingsScreenProps {
  readonly scope: SettingsScope;
  readonly group: string | undefined;
  readonly groupLink: (group: string) => GroupLink;
  /** The landing (`/settings` with no group); without one, the screen opens on its first group. */
  readonly landingLink?: GroupLink;
  /** The page's heading; "Settings" by default. */
  readonly heading?: string;
  /** Between the heading and the groups: HQ's sections of Platform settings, and who is being looked at. */
  readonly header?: ReactNode;
  /** Where a value comes from, in this screen's words. */
  readonly sources?: SourceWords;
  /** Why a setting on this screen cannot be set at its scope (HQ's screens). */
  readonly notSetHere?: string;
  /** The screen's other pages its notes link to. */
  readonly links?: ScreenLinks;
  /** The organisation's settings, for a row set there (a venue's screen, for a member who may open them). */
  readonly organizationLink?: OrganizationLink;
}

/** The job groups a screen shows: those with a row here, and on a venue's screen those with a panel of their own. */
function groupsOf(view: SettingsView | undefined, scope: SettingsScope): JobGroupId[] {
  const present = new Set<string>();
  for (const entry of view?.settings ?? []) present.add(placementOf(entry.key, entry.group).group);
  if (scope.kind === 'venue') for (const id of ['venue', 'money', 'cashAndShifts', 'hardware'] as const) present.add(id);
  if (scope.kind === 'organization') present.add('money');
  return JOB_GROUP_IDS.filter((id) => present.has(id));
}

export function SettingsScreen({ scope, group, groupLink, landingLink, heading, header, sources, notSetHere, links = {}, organizationLink }: SettingsScreenProps) {
  const { t } = useTranslation('console');
  const { query } = useSettingsView(scope);
  const navigate = useNavigate();
  const title = heading ?? t('settings.title');
  const hash = useRouterState({ select: (state) => state.location.hash });
  const hashKey = settingKeyFromHash(hash);
  // The key to bring into view: the palette's or the search's latest, else the URL's. A new hash starts over.
  const [revealed, setRevealed] = useState<{ readonly key: string; readonly hash: string } | undefined>(undefined);
  const reveal = revealed !== undefined && revealed.hash === hash.replace(/^#/, '') ? revealed.key : hashKey;
  useEffect(() => {
    const onReveal = (event: Event) => {
      const key = (event as CustomEvent<string>).detail;
      if (typeof key === 'string') setRevealed({ key, hash: window.location.hash.replace(/^#/, '') });
    };
    window.addEventListener(REVEAL_SETTING_EVENT, onReveal);
    return () => window.removeEventListener(REVEAL_SETTING_EVENT, onReveal);
  }, []);

  const view = query.data?.data;
  const groups = useMemo(() => groupsOf(view, scope), [view, scope]);
  const docs = useSearchDocs(scope, view);

  const choose = (doc: SettingDoc) => {
    const link = groupLink(doc.group);
    const again = settingKeyFromHash(window.location.hash) === doc.anchor && group === doc.group;
    void navigate({ to: link.to, params: link.params, hash: doc.anchor } as never).then(() => {
      if (again) requestRevealSetting(doc.anchor);
    });
  };

  if (group === undefined) {
    if (query.isPending) return <Loading />;
    if (view === undefined) return <LoadFailure error={query.error} message={t('settings.unavailable')} onRetry={() => void query.refetch()} />;
    const first = groups[0];
    if (first === undefined) {
      return (
        <SettingsFrame groups={[]} current={undefined} groupLink={groupLink} landingLink={landingLink} heading={title} header={header} title={title} search={null}>
          <p>{t('settings.empty')}</p>
        </SettingsFrame>
      );
    }
    if (landingLink === undefined) {
      const link = groupLink(first);
      return <Navigate to={link.to} params={link.params} replace />;
    }
    return (
      <SettingsFrame
        groups={groups}
        current={undefined}
        groupLink={groupLink}
        landingLink={landingLink}
        heading={title}
        header={header}
        title={t('settings.landing.groupsTitle')}
        search={<SettingsSearchBox docs={docs} onChoose={choose} />}
      >
        {scope.kind === 'venue' ? (
          <SetupSummary organizationId={scope.organizationId} venueId={scope.venueId} venueSlug={links.venueProfile?.params['venueSlug'] ?? ''} />
        ) : null}
        <GroupCards groups={groups} view={view} scope={scope} groupLink={groupLink} />
      </SettingsFrame>
    );
  }

  // An old registry group in the URL: the job group its key — or the group itself — belongs to now.
  if (!isJobGroup(group)) {
    const mapped = jobGroupOf(group, hashKey);
    if (mapped !== undefined) {
      const link = groupLink(mapped);
      return <Navigate to={link.to} params={link.params} hash={hash === '' ? undefined : hash.replace(/^#/, '')} replace />;
    }
  }

  const groupHeading = t(`settings.group.${group}`, { defaultValue: group });
  let content: ReactNode;
  if (scope.kind === 'venue' && group === 'venue') {
    // The venue record is its own resource: the profile is drawn whether or not the settings could be read.
    content = <VenueProfileGroup organizationId={scope.organizationId} venueId={scope.venueId} settings={query.data} />;
  } else if (view === undefined) {
    content = query.isError ? <LoadFailure error={query.error} message={t('settings.unavailable')} onRetry={() => void query.refetch()} /> : <Loading />;
  } else {
    content = (
      <RegistryGroup
        scope={scope}
        group={group}
        view={query.data as Versioned<SettingsView>}
        links={links}
        reveal={reveal}
        {...(sources === undefined ? {} : { sources })}
        {...(notSetHere === undefined ? {} : { notSetHere })}
        {...(organizationLink === undefined ? {} : { organizationLink })}
      />
    );
  }

  return (
    <SettingsFrame
      groups={groups}
      current={group}
      groupLink={groupLink}
      landingLink={landingLink}
      heading={title}
      header={header}
      title={groupHeading}
      search={<SettingsSearchBox docs={docs} onChoose={choose} />}
    >
      {content}
    </SettingsFrame>
  );
}

/** The landing's group cards: each group's name, what is in it, and how many settings. */
function GroupCards({
  groups,
  view,
  scope,
  groupLink,
}: {
  readonly groups: readonly JobGroupId[];
  readonly view: SettingsView;
  readonly scope: SettingsScope;
  readonly groupLink: (group: string) => GroupLink;
}) {
  const { t } = useTranslation('console');
  const counts = new Map<string, number>();
  for (const entry of view.settings) {
    const id = placementOf(entry.key, entry.group).group;
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  const venueFields = scope.kind === 'venue' ? FIELDS.length : 0;
  return (
    <ul className="console-settings-cards" aria-label={t('settings.groupsNav')}>
      {groups.map((id) => {
        const link = groupLink(id);
        const count = (counts.get(id) ?? 0) + (id === 'venue' ? venueFields : 0);
        return (
          <li key={id} className="console-settings-card">
            <Link to={link.to} params={link.params} className="console-settings-card__link">
              <span className="console-settings-card__title">{t(`settings.group.${id}`)}</span>
            </Link>
            <span className="console-settings-card__hint">{t(`settings.groupHint.${id}`)}</span>
            {count > 0 ? <span className="console-settings-card__count console-muted">{t('settings.landing.count', { count })}</span> : null}
          </li>
        );
      })}
    </ul>
  );
}

/** Every setting on this screen, for the search at its top — the venue record's fields and the panels too. */
function useSearchDocs(scope: SettingsScope, view: SettingsView | undefined): readonly SettingDoc[] {
  const { t } = useTranslation('console');
  const labels = useRegistryLabels();
  const helpOf = useSettingHelp();
  return useMemo(() => {
    if (view === undefined) return [];
    const target: SettingsTarget = { id: 'here', scope, scopeLabel: '', to: '', params: {}, venueProfile: scope.kind === 'venue' };
    const words: IndexWords = {
      label: (key) => labels.field(key, []),
      help: helpOf,
      options: (entry) => optionWords(entry, labels),
      groupLabel: (group) => t(`settings.group.${group}`, { defaultValue: group }),
      venueField: (key) => ({ label: t(`venueProfile.label.${key}`, { defaultValue: key }), description: t(`venueProfile.description.${key}`, { defaultValue: '' }) }),
    };
    const docs = buildSettingsIndex([{ target, view }], scope.kind === 'venue' ? FIELDS.map((field) => field.key) : [], words);
    // The panels with their own endpoints: found by their name and what they hold.
    const panels: { key: string; group: JobGroupId; section: string; text: string }[] =
      scope.kind === 'venue'
        ? [
            { key: 'panel.counters', group: 'cashAndShifts', section: 'counters', text: t('settings.counters.intro') },
            { key: 'panel.printers', group: 'hardware', section: 'printers', text: t('printers.hint') },
            { key: 'panel.taxRegistration', group: 'money', section: 'tax', text: t('settings.help.tax__mode') },
            { key: 'panel.taxCategories', group: 'money', section: 'taxCategories', text: t('taxCategories.hint') },
          ]
        : scope.kind === 'organization'
          ? [
              { key: 'panel.businessDetails', group: 'money', section: 'businessDetails', text: t('businessDetails.hint') },
              { key: 'panel.taxCategories', group: 'money', section: 'taxCategories', text: t('taxCategories.hint') },
            ]
          : [];
    return [
      ...docs,
      ...panels.map((panel) => ({
        id: `here:${panel.key}`,
        key: panel.key,
        label: t(`settings.section.${panel.section}`),
        description: panel.text,
        group: panel.group,
        groupLabel: t(`settings.group.${panel.group}`),
        anchor: panel.key,
        target,
        readOnly: false,
      })),
    ];
  }, [scope, view, labels, helpOf, t]);
}

function SettingsFrame({
  groups,
  current,
  groupLink,
  landingLink,
  heading,
  header,
  title,
  search,
  children,
}: {
  readonly groups: readonly string[];
  readonly current: string | undefined;
  readonly groupLink: (group: string) => GroupLink;
  readonly landingLink: GroupLink | undefined;
  readonly heading: string;
  readonly header: ReactNode;
  readonly title: string;
  readonly search: ReactNode;
  readonly children: ReactNode;
}) {
  const { t } = useTranslation('console');
  const groupRef = useRef<HTMLElement>(null);
  useRevealSetting(groupRef);
  return (
    <div className="console-page">
      <PageHeader title={heading} description={t('settings.intro')} />
      {header}
      {search}
      <div className="console-settings">
        {groups.length > 0 ? (
          <nav className="console-settings__groups" aria-label={t('settings.groupsNav')}>
            <ul>
              {landingLink === undefined ? null : (
                <li>
                  {/* Exact: the landing's path is a parent of every group's, and must not read as current on them. */}
                  <Link
                    to={landingLink.to}
                    params={landingLink.params}
                    activeOptions={{ exact: true }}
                    className="console-settings__link"
                    aria-current={current === undefined ? 'page' : undefined}
                  >
                    {t('settings.landing.groupsTitle')}
                  </Link>
                </li>
              )}
              {groups.map((id) => {
                const link = groupLink(id);
                return (
                  <li key={id}>
                    <Link to={link.to} params={link.params} className="console-settings__link" aria-current={id === current ? 'page' : undefined}>
                      {t(`settings.group.${id}`, { defaultValue: id })}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>
        ) : null}
        <section className="console-settings__group" aria-labelledby="console-group-title" ref={groupRef}>
          <h2 id="console-group-title" className="console-settings__title">
            {title}
          </h2>
          {children}
        </section>
      </div>
    </div>
  );
}

/**
 * A setting named by the URL's hash (`…/settings/money#wallet.minTopupAmount`) — a shared link, the search, or the
 * command palette's choice — is scrolled to, focused and marked once its row is drawn (`reveal.ts`). The palette's
 * event covers choosing the setting already in the address bar.
 */
function useRevealSetting(root: RefObject<HTMLElement | null>) {
  const hash = useRouterState({ select: (state) => state.location.hash });
  useEffect(() => {
    const key = settingKeyFromHash(hash);
    const element = root.current;
    if (key === undefined || element === null) return undefined;
    return revealWhenDrawn(element, key);
  }, [hash, root]);
  useEffect(() => {
    let cancel: (() => void) | undefined;
    const onReveal = (event: Event) => {
      const key = (event as CustomEvent<string>).detail;
      const element = root.current;
      if (typeof key !== 'string' || element === null) return;
      cancel?.();
      cancel = revealWhenDrawn(element, key);
    };
    window.addEventListener(REVEAL_SETTING_EVENT, onReveal);
    return () => {
      window.removeEventListener(REVEAL_SETTING_EVENT, onReveal);
      cancel?.();
    };
  }, [root]);
}

/** A group drawn from the registry, with its sections' panels. */
export function RegistryGroup({
  scope,
  group,
  view,
  sources,
  notSetHere,
  links,
  reveal,
  organizationLink,
}: {
  readonly scope: SettingsScope;
  readonly group: string;
  readonly view: Versioned<SettingsView>;
  readonly sources?: SourceWords;
  readonly notSetHere?: string;
  readonly links: ScreenLinks;
  readonly reveal: string | undefined;
  readonly organizationLink?: OrganizationLink;
}) {
  const { t } = useTranslation('console');
  const api = useApi();
  const queryClient = useQueryClient();
  const labels = useRegistryLabels();
  const helpOf = useSettingHelp();
  const formatter = useVenueFormatter();
  const key = settingsKey(scope);
  const classNames = useClassNames(scope);
  const venueNames = useVenueNames(scope);

  // A master on another screen — wallets, set by the organisation — read from there on a venue's screen.
  const missingMasters = useMemo(() => {
    const here = new Set(view.data.settings.map((entry) => entry.key));
    return new Set(
      view.data.settings
        .filter((entry) => placementOf(entry.key, entry.group).group === group)
        .flatMap((entry) => (placementOf(entry.key, entry.group).dependsOn ?? []).map((dependency) => dependency.key))
        .filter((master) => !here.has(master)),
    );
  }, [view, group]);
  const organizationScope = useMemo<SettingsScope>(() => ({ kind: 'organization', organizationId: scope.kind === 'platform' ? '' : scope.organizationId }), [scope]);
  const organizationView = useSettingsView(organizationScope, scope.kind === 'venue' && missingMasters.size > 0).query.data?.data;
  const externalValueOf = (master: string) => organizationView?.settings.find((entry) => entry.key === master)?.value;

  const part = useMemo(() => {
    const words: RegistryWords = {
      label: (settingKey) => labels.field(settingKey, []),
      help: helpOf,
      section: (id) => t(`settings.section.${id}`, { defaultValue: id }),
      className: (id) => classNames.get(id),
      venueName: (id) => venueNames.get(id),
      ...(notSetHere === undefined ? {} : { notSetHere }),
    };
    return registryPart({ api, scope, group, view, words, formatter, onVersion: (next) => queryClient.setQueryData(key, next) });
  }, [api, scope, group, view, labels, helpOf, t, classNames, venueNames, formatter, queryClient, key, notSetHere]);

  const layout = layoutGroup(group);
  const sections = (layout?.sections ?? []).map((section) => ({ id: section.id, title: t(`settings.section.${section.id}`, { defaultValue: section.id }) }));
  const organizationId = scope.kind === 'platform' ? undefined : scope.organizationId;
  const currency = view.data.scope.currency;
  const money = currency ? settingMoney(formatter, currency) : undefined;
  const panelOf = (id: string, which: 'panel' | 'panelAfter' = 'panel'): ReactNode => {
    const section = layout?.sections.find((candidate) => candidate.id === id);
    const panel = which === 'panel' && scope.kind === 'organization' && section?.organizationPanel !== undefined ? section.organizationPanel : section?.[which];
    switch (panel) {
      case 'payg':
        return scope.kind === 'venue' || scope.kind === 'organization' ? <PaygSummary view={view.data} link={links.pricing} /> : null;
      case 'businessDetails':
        return scope.kind === 'organization' ? (
          <div className="console-settings__panel" data-setting="panel.businessDetails" tabIndex={-1}>
            <BusinessDetailsGroup organizationId={scope.organizationId} />
          </div>
        ) : null;
      case 'taxRegistration':
        return scope.kind === 'venue' ? (
          <div className="console-settings__panel" data-setting="panel.taxRegistration" tabIndex={-1}>
            <TaxRegistrationGroup organizationId={scope.organizationId} venueId={scope.venueId} />
          </div>
        ) : null;
      case 'taxCategories':
        return organizationId === undefined || scope.kind === 'hqOrganization' ? null : (
          <div className="console-settings__panel" data-setting="panel.taxCategories" tabIndex={-1}>
            <TaxCategoriesGroup organizationId={organizationId} />
          </div>
        );
      case 'onlinePaymentsStatus':
        return scope.kind === 'venue' ? <OnlinePaymentsStatusLine organizationId={scope.organizationId} venueId={scope.venueId} /> : null;
      case 'counters':
        return scope.kind === 'venue' ? (
          <div className="console-settings__panel" data-setting="panel.counters" tabIndex={-1}>
            <CountersGroup organizationId={scope.organizationId} venueId={scope.venueId} />
          </div>
        ) : null;
      case 'printers':
        return scope.kind === 'venue' ? (
          <div className="console-settings__panel" data-setting="panel.printers" tabIndex={-1}>
            <PrintersGroup organizationId={scope.organizationId} venueId={scope.venueId} />
          </div>
        ) : null;
      default:
        return null;
    }
  };
  // The notes at the head of a section's card: how its settings work together.
  const noteOf = (id: string): ReactNode => {
    switch (layout?.sections.find((section) => section.id === id)?.panel) {
      case 'daysAndShifts':
        return <DaysNote scope={scope} venueProfile={links.venueProfile} />;
      case 'offlinePlay':
        return <OfflinePlayNote />;
      case 'limits':
        return <LimitsNote link={links.roles} />;
      case 'ageRules':
        return <AgeRulesNote link={links.classes} />;
      default:
        return null;
    }
  };
  const after = (id: string, valueOf: (key: string) => unknown): ReactNode => {
    if (id === 'wallet' && money !== undefined && part.entries.some((entry) => entry.key === 'wallet.topupPresets' || entry.key === 'wallet.topupOffers')) {
      return <TopUpPreview valueOf={valueOf} money={money} />;
    }
    if (id === 'cleanup' && valueOf('cleanup.profile') === 'off') return <CleanupOffNote profile="off" />;
    return null;
  };

  const hasPanels = (layout?.sections ?? []).some((section) =>
    [section.panel, section.panelAfter].some(
      (panel) => panel === 'payg' || panel === 'businessDetails' || panel === 'taxRegistration' || panel === 'taxCategories' || panel === 'counters' || panel === 'printers',
    ),
  );
  if (part.entries.length === 0 && !hasPanels) return <p>{t('settings.empty')}</p>;
  const editable = part.entries.some((entry) => entry.editable);
  // Each row that cannot be changed says why — a lock, a scope it is not set at, the role — so the notice above the
  // form is needed only when none does.
  const explained = part.entries.every((entry) => entry.lockedAt !== undefined || entry.readOnlyReason !== undefined || entry.why !== undefined);
  return (
    <SettingsForm
      // Keyed by scope and group: another screen's form is another form, never this one's draft.
      key={`${key.join('/')}:${group}`}
      parts={[part]}
      labels={labels}
      custom={customControlsFor(organizationId)}
      sections={sections}
      slots={{ before: panelOf, note: noteOf, after, end: (id) => panelOf(id, 'panelAfter') }}
      externalValueOf={externalValueOf}
      externalLabel={(settingKey) => labels.field(settingKey, [])}
      {...(sources === undefined ? {} : { sources })}
      {...(organizationLink === undefined ? {} : { organizationLink })}
      {...(reveal === undefined ? {} : { reveal })}
      {...(links.classes === undefined ? {} : { classesLink: links.classes })}
      notice={editable || explained || part.entries.length === 0 ? undefined : t('settings.readOnly')}
    />
  );
}

/** A venue's station classes by id, to name those that keep their own value (spec/13 §12). */
function useClassNames(scope: SettingsScope): ReadonlyMap<string, string> {
  const venue = scope.kind === 'venue' ? scope : undefined;
  const classes = useStationClassesWhen(venue?.organizationId ?? '', venue?.venueId ?? '', venue !== undefined);
  return useMemo(() => new Map((classes ?? []).map((klass) => [klass.id, klass.name])), [classes]);
}

function useStationClassesWhen(organizationId: string, venueId: string, enabled: boolean) {
  const query = useStationClasses(organizationId, venueId, enabled);
  return query.data;
}

/** The organisation's venues by id, to name those that keep their own value on its screen. */
function useVenueNames(scope: SettingsScope): ReadonlyMap<string, string> {
  const venues = useMyVenues(scope.kind === 'organization');
  return useMemo(
    () => new Map((venues.data ?? []).filter((venue) => scope.kind === 'organization' && venue.organizationId === scope.organizationId).map((venue) => [venue.id, venue.name])),
    [venues.data, scope],
  );
}

const customControls = new Map<string, Readonly<Record<string, ComponentType<ControlProps>>>>();

/** The custom controls of a registry screen — the top-up rows, and the organisation's tax category picker — one stable set per organisation. */
function customControlsFor(organizationId: string | undefined): Readonly<Record<string, ComponentType<ControlProps>>> {
  const id = organizationId ?? '';
  let found = customControls.get(id);
  if (found === undefined) {
    found = { ...TOPUP_CONTROLS, ...(organizationId === undefined ? {} : taxCategoryControlFor(organizationId)) };
    customControls.set(id, found);
  }
  return found;
}
